import { CardCategory } from '@api/swagger/definitions/backoffice/index.ts'
import Box from '@mui/material/Box'
import LoadingButton from '@mui/material/Button'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { usePatchCardV1, usePersons } from 'api/react-query'
import useMaterialNotification from 'hooks/useMaterialNotification.tsx'
import { When } from 'mynt-components/components/When'
import { memo, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { CardType } from '../../api/cards/types.ts'
import { AutoCompleteController, SelectController } from '../../components/react-hook-components.tsx'

interface CustomerCardTabOverviewSingleCardDetailsCategoryProps {
  personId: string
  customerId: string
  cardDetails: { cardId: string; cardType: CardType }
  onClose: () => void
}

export const CustomerCardTabOverviewSingleCardDetailsCategory = memo(function CustomerCardTabOverviewSingleCardDetailsCategory({
  personId,
  customerId,
  cardDetails,
  onClose,
}: CustomerCardTabOverviewSingleCardDetailsCategoryProps) {
  // --- State & Refs (useState, useRef) ---
  const [loaders, _setLoaders] = useState({
    category: false,
    cardHolder: false,
  })

  const isSubmittingRef = useRef(false)

  const setLoader = (loader: keyof typeof loaders, value = true) => _setLoaders((loaders) => ({ ...loaders, [loader]: value }))

  const form = useForm({
    defaultValues: {
      category: cardDetails.cardType,
      personId: personId,
    },
    mode: 'onBlur',
  })

  const isPossibleToChangeCategory = cardDetails.cardType === CardType.VIRTUAL_CORPORATE || cardDetails.cardType === CardType.SUBSCRIPTION
  const isSubscriptionCard = cardDetails.cardType === CardType.SUBSCRIPTION

  const showCategoryChangeConfirmation = form.watch('category') !== cardDetails.cardType
  const showCardHolderChangeConfirmation = form.watch('personId') !== personId

  const categoryPatchOptions = [CardType.VIRTUAL_CORPORATE, CardType.SUBSCRIPTION].map((category) => ({
    label: category,
    value: category,
  }))

  // --- Custom Hooks ---
  const { data: persons = [] } = usePersons(customerId)
  const cardMutation = usePatchCardV1(customerId, cardDetails.cardId)
  const notify = useMaterialNotification()

  // --- Derived State (useMemo) ---
  const personOptions = useMemo(() => persons.map(({ id, fullName }) => ({ value: id, label: fullName })), [persons])

  // --- Effects (useEffect) ---

  // --- Event Handlers (useCallback) ---
  const handleOnSubmit = (loader: keyof typeof loaders) => (e?: React.BaseSyntheticEvent) => {
    if (isSubmittingRef.current) {
      return
    }
    isSubmittingRef.current = true

    return form.handleSubmit(async (data) => {
      setLoader(loader, true)

      try {
        if (loader === 'category') {
          await cardMutation.mutateAsync({
            category: data.category === CardType.SUBSCRIPTION ? CardCategory.SUBSCRIPTION : CardCategory.STANDARD,
          })
          notify('Updated card category')
        }

        if (loader === 'cardHolder') {
          await cardMutation.mutateAsync({
            personId: data.personId,
          })
          notify('Updated card holder')
        }
      } catch (error) {
        console.warn(error)
      } finally {
        setLoader(loader, false)
        onClose()
      }
    })(e)
  }

  // --- Render ---

  return (
    <>
      <Box sx={{ display: 'flex', gap: '32px', flexDirection: 'row', padding: '16px 0' }}>
        <When is={!isPossibleToChangeCategory}>
          <Typography variant="body2">Card category: {cardDetails.cardType}</Typography>
        </When>
        <When is={isPossibleToChangeCategory}>
          <Box sx={{ minWidth: '400px' }}>
            <SelectController
              disabled={loaders.category || showCardHolderChangeConfirmation}
              control={form.control}
              name="category"
              labelText="Card category"
              options={categoryPatchOptions}
            />
          </Box>
        </When>
        <ConfirmationActions
          isLoadingConfirmAction={loaders.category}
          isVisible={showCategoryChangeConfirmation}
          label="I want to confirm changing card category"
          onCancel={() => form.setValue('category', cardDetails.cardType)}
          onConfirm={handleOnSubmit('category')}
        />
      </Box>
      <When is={isSubscriptionCard}>
        <Box sx={{ display: 'flex', gap: '32px', flexDirection: 'row', padding: '16px 0' }}>
          <Box sx={{ minWidth: '400px' }}>
            <AutoCompleteController
              control={form.control}
              name="personId"
              options={personOptions}
              label="Change cardholder"
              disabled={loaders.cardHolder || showCategoryChangeConfirmation}
            />
          </Box>
          <ConfirmationActions
            isLoadingConfirmAction={loaders.cardHolder}
            isVisible={showCardHolderChangeConfirmation}
            label="I want to confirm changing card holder"
            onCancel={() => form.setValue('personId', personId)}
            onConfirm={handleOnSubmit('cardHolder')}
          />
        </Box>
      </When>
    </>
  )
})

interface ConfirmationActionsProps {
  isLoadingConfirmAction: boolean
  isVisible: boolean
  label: string
  onCancel: () => void
  onConfirm: () => void
}

const ConfirmationActions = ({ isLoadingConfirmAction, isVisible, label, onCancel, onConfirm }: ConfirmationActionsProps) => {
  const [checked, setChecked] = useState<boolean>(false)

  const handleCancel = () => {
    setChecked(false)
    onCancel()
  }

  return (
    <When is={isVisible}>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={checked}
              disabled={isLoadingConfirmAction}
              onChange={(event) => {
                setChecked(event.target.checked)
              }}
            />
          }
          label={<Typography variant="body2">{label}</Typography>}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: '32px', flexDirection: 'row', mt: 1 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={isLoadingConfirmAction}>
          Cancel
        </Button>
        <LoadingButton disabled={!checked || isLoadingConfirmAction} variant="primary" onClick={onConfirm} loading={isLoadingConfirmAction}>
          Confirm
        </LoadingButton>
      </Box>
    </When>
  )
}
